'use es6';

import I18n from 'I18n';
import ContentRoutes from 'ContentUtils/Routes';
import { createSelector } from 'reselect';
import { basicSelector } from 'ContentEditorUI/redux/selectors/helpers';
import { getActiveDomain, getFullDomainPath, getAvailableDomainsForPage, getCanOnlyChangeToPrimaryDomain } from 'ContentEditorUI/redux/selectors/portalDomainsSelectors';
import { getUseRssHeadlineAsSubject } from 'EmailEditorUI/redux/selectors/BlogSelectors';
import { getIsBlogOrRssEmail, getIsSubscriptionEmail, getIsMembershipEmail } from 'EmailEditorUI/redux/selectors/EmailSelectors';
import { getIsDragDropEmail } from 'EmailEditorUI/redux/selectors/emailCategoriesSelectors';
import { getContentModelHtmlTitle, getContentModelId, getContentModelPageExpiryDate, getContentModelPageExpiryEnabled, getContentModelPageExpiryRedirectUrl, getPreviewKey, getPublishDate } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { hasWebVersionLinkModule } from 'EmailEditorUI/redux/selectors/ModuleSelectors';
import { getHasFreeEmail, getHasEmailHostingDomainsAccess, getHasEmailDomainAccess, getHasFullEditorAccess, getHasEmailSendingStandardScope, getIsUngatedForPageExpiryValidator, getIsUngatedForBEValidation } from 'EmailEditorUI/redux/selectors/AuthSelectors';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { buildFullUrl, isTemporarySlug } from 'ContentEditorUI/utils/contentModelURLUtils';
import { getSiteSettingsBodyOverrideByDomain } from 'ContentEditorUI/redux/selectors/siteSettingsSelectors';
import { getDomainValidation, getHasSlugConflict } from 'EmailEditorUI/redux/selectors/ApiValidationSelectors';
import { getBasePreviewUrl } from 'EmailEditorUI/redux/selectors/previewSelectors';
import { getIsWebversionEnabledOnContentModel } from 'EmailEditorUI/redux/selectors/EditSelectors';
import { getIsTrialWithOnlyCustomDomains } from 'EmailEditorUI/redux/selectors/domainSelectors';
import { getHasMarketingEmailTrial, getMarketingEmailTrialType } from 'EmailData/selectors/portalTrialSelectors';
import { getDisplayForDomain } from 'ContentEditorUI/utils/domainsUtils';
import { getEmailPrimaryDomain } from 'EmailEditorUI/redux/selectors/emailPortalDomainsSelectors';
import { getFieldErrorMap } from 'EmailEditorUI/redux/selectors/ApiValidationSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
const MAXIMUM_SLUG_LENGTH = 175;
const WEB_REGEX = new RegExp(/[-a-zA-Z0-9@:%_+.~#?&//=]{2,256}\.[a-z]{2,18}\b(\/[-a-zA-Z0-9@:%_+.~#?&//=]*)?/gi);
export const getPageSettings = basicSelector(state => state.pageSettings);
export const getIsWebVersionSupportedByTemplate = basicSelector(state => state.contentSchema.context.has_view_as_page_url || getIsDragDropEmail(state));
export const getIsWebversionEnabled = createSelector([getIsDragDropEmail, getIsWebversionEnabledOnContentModel, hasWebVersionLinkModule], (isDragDrop, enabled, hasWebversionModule) => isDragDrop ? hasWebversionModule : enabled);
export const getEmailTypeSupportsWebversion = createSelector([getIsSubscriptionEmail, getIsMembershipEmail], (isSubscriptionEmail, isMembershipEmail) => !isSubscriptionEmail && !isMembershipEmail);
export const canEnableWebversion = createSelector([getHasFreeEmail, getEmailTypeSupportsWebversion], (hasFreeEmail, emailTypeSupportsWebversion) => emailTypeSupportsWebversion && !hasFreeEmail);
export const canUpdateDomain = createSelector([getIsSubscriptionEmail, getHasEmailDomainAccess], (isSubscriptionEmail, hasAccess) => {
  return hasAccess && !isSubscriptionEmail;
});
const createWebversionSelector = field => createSelector([getPageSettings], pageSettings => pageSettings[field]);
export const getPageSettingsShowAllErrors = createWebversionSelector('showAllErrors');
export const getPageSettingsSlugRaw = createWebversionSelector('slugRaw');
export const getPageSettingslinkSlugToTitle = createWebversionSelector('linkSlugToTitle');
export const getPageSettingsPageExpiryRedirectName = createWebversionSelector('pageExpiryRedirectName');
export const getPageSettingsFullUrl = createSelector([getSiteSettingsBodyOverrideByDomain, getActiveDomain, getPageSettingsSlugRaw], buildFullUrl);
const getPublishedURLFromModel = createSelector([getFullDomainPath, getPageSettingsSlugRaw], (fullDomainPath, slug) => {
  // TODO Handle for blog posts too
  if (slug && slug.startsWith('/')) {
    return fullDomainPath + slug;
  }
  return `${fullDomainPath}/${slug}`;
});
export const getShareablePreviewURL = createSelector([getContentModelId, getPreviewKey, getPublishedURLFromModel, getHasEmailHostingDomainsAccess, getBasePreviewUrl], (id, previewKey, publishedUrl, hasEmailHostingDomainsAccess, previewUrl) => {
  if (!hasEmailHostingDomainsAccess) {
    return previewUrl;
  }
  return `${publishedUrl}?hs_preview=${previewKey}-${id}`;
});
export const WEBVERSION_VALIDATION_ERRORS = {
  PAGE_EXPIRY_REDIRECT_ID: {
    pane: 'settings',
    type: 'required',
    cause: 'noRedirectURL'
  },
  PAGE_EXPIRY_INVALID_URL: {
    pane: 'settings',
    type: 'invalid',
    cause: 'invalidRedirectURL',
    target: 'pageExpiryRedirectUrl',
    translated: true,
    message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "editors.pageExpiryRedirectUrl.invalid"
    })
  },
  PAGE_EXPIRY_DATE: {
    pane: 'settings',
    type: 'required',
    cause: 'noExpiryDate',
    target: 'pageExpiryDate',
    translated: true,
    message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "editors.pageExpiryDate.missing"
    })
  },
  PAGE_EXPIRY_DATE_PUBLISH_BEFORE_EXPIRY: {
    pane: 'settings',
    type: 'invalid',
    cause: 'invalidExpiryDate',
    target: 'pageExpiryDate',
    translated: true,
    message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "emailSettings.webVersion.expiryDate.invalid"
    })
  }
};
const PAGE_EXPIRY_VALIDATION_CONFIG = {
  pageExpiryRedirectUrl: {
    fieldName: 'PAGE_EXPIRY_REDIRECT_ID',
    errorMappings: {
      NOT_SET: WEBVERSION_VALIDATION_ERRORS.PAGE_EXPIRY_REDIRECT_ID,
      PAGE_EXPIRY_INVALID_URL: WEBVERSION_VALIDATION_ERRORS.PAGE_EXPIRY_INVALID_URL
    }
  },
  pageExpiryDate: {
    fieldName: 'PAGE_EXPIRY_DATE',
    errorMappings: {
      NOT_SET: WEBVERSION_VALIDATION_ERRORS.PAGE_EXPIRY_DATE,
      PAGE_EXPIRY_DATE_PUBLISH_BEFORE_EXPIRY: WEBVERSION_VALIDATION_ERRORS.PAGE_EXPIRY_DATE_PUBLISH_BEFORE_EXPIRY
    }
  }
};
export const getPageExpiryServerValidatorErrors = createSelector([getFieldErrorMap], fieldErrorMap => {
  const errors = {};
  Object.entries(PAGE_EXPIRY_VALIDATION_CONFIG).forEach(([targetProperty, {
    fieldName,
    errorMappings
  }]) => {
    const fieldErrors = fieldErrorMap === null || fieldErrorMap === void 0 ? void 0 : fieldErrorMap[fieldName];
    if (fieldErrors !== null && fieldErrors !== void 0 && fieldErrors.length) {
      for (const [errorKey, errorMessage] of Object.entries(errorMappings)) {
        if (fieldErrors.some(error => error.errorKey === errorKey)) {
          errors[targetProperty] = errorMessage;
        }
      }
    }
  });
  return errors;
});
export const getWebversionValidationErrors = createSelector([canEnableWebversion, getIsWebversionEnabled, getContentModelHtmlTitle, getContentModelPageExpiryEnabled, getContentModelPageExpiryDate, getPageSettingsSlugRaw, getPublishDate, getIsBlogOrRssEmail, getUseRssHeadlineAsSubject, getContentModelPageExpiryRedirectUrl, getHasSlugConflict, getIsTrialWithOnlyCustomDomains, getHasFullEditorAccess, getIsUngatedForPageExpiryValidator, getPageExpiryServerValidatorErrors, getFieldErrorMap, getIsUngatedForBEValidation], (hasWebversionAccess, enabled, htmlTitle, pageExpiryEnabled, pageExpiryDate, slugRaw, publishDate, isRSSEmail, useRssHeadlineAsSubject, pageExpiryRedirectUrl, hasSlugConflict, isTrialWithOnlyCustomDomains, hasFullEditorAccess, isUngatedForPageExpiryValidator, pageExpiryServerValidatorErrors, fieldErrorMap, isUngatedForBEValidation) => {
  var _fieldErrorMap$SLUG;
  if (!enabled || !hasWebversionAccess) {
    return {};
  }
  const errors = {};
  if (isTrialWithOnlyCustomDomains) {
    errors.disabledWhileOnTrial = {
      pane: 'content',
      type: 'invalid',
      cause: 'disabledWhileOnTrial',
      translated: true,
      message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "panes.review.sections.settings.items.webversion.error.disabledWhileOnTrial"
      }),
      target: 'content'
    };
    return errors;
  }
  if (!htmlTitle && !useRssHeadlineAsSubject) {
    errors.htmlTitle = {
      pane: 'settings',
      type: 'required',
      cause: 'noHtmlTitle'
    };
  }
  if (isUngatedForBEValidation && (_fieldErrorMap$SLUG = fieldErrorMap['SLUG']) !== null && _fieldErrorMap$SLUG !== void 0 && _fieldErrorMap$SLUG.some(e => e.errorKey === 'NOT_SET')) {
    errors.slug = {
      pane: 'settings',
      type: 'required',
      cause: 'slugRequired',
      translated: true,
      message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "emailSettings.webVersion.slug.missing"
      })
    };
  } else if ((!slugRaw || isTemporarySlug(slugRaw)) && !(isRSSEmail && useRssHeadlineAsSubject)) {
    errors.slug = {
      pane: 'settings',
      type: 'required',
      cause: 'slugRequired',
      translated: true,
      message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "emailSettings.webVersion.slug.missing"
      })
    };
  } else if (slugRaw && slugRaw.length > MAXIMUM_SLUG_LENGTH) {
    errors.slug = {
      type: 'required',
      cause: 'exceedsMaxLength',
      translated: true,
      message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "emailSettings.webVersion.slug.exceedsMaxLength",
        options: {
          count: slugRaw.length
        }
      })
    };
  } else if (hasSlugConflict.error) {
    errors.slug = {
      pane: 'settings',
      type: 'invalid',
      cause: 'slugConflict',
      target: 'slug',
      useHtml: true,
      briefMessageKey: 'panes.review.sections.settings.items.webversion.error.slugConflict',
      message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "emailSettings.webVersion.slug.conflict"
      })
    };
  }
  if (!isUngatedForPageExpiryValidator) {
    if (pageExpiryEnabled && hasFullEditorAccess) {
      if (!pageExpiryDate) {
        errors.pageExpiryDate = WEBVERSION_VALIDATION_ERRORS.PAGE_EXPIRY_DATE;
      } else if (I18n.moment(pageExpiryDate).isBefore(I18n.moment.now()) || I18n.moment(pageExpiryDate).isBefore(I18n.moment(publishDate))) {
        errors.pageExpiryDate = WEBVERSION_VALIDATION_ERRORS.PAGE_EXPIRY_DATE_PUBLISH_BEFORE_EXPIRY;
      }
    }
    if (pageExpiryEnabled) {
      if (!pageExpiryRedirectUrl || !pageExpiryRedirectUrl.trim()) {
        errors.pageExpiryRedirectUrl = WEBVERSION_VALIDATION_ERRORS.PAGE_EXPIRY_REDIRECT_ID;
      } else if (!pageExpiryRedirectUrl.match(WEB_REGEX)) {
        errors.pageExpiryRedirectUrl = WEBVERSION_VALIDATION_ERRORS.PAGE_EXPIRY_REDIRECT_ID_INVALID_URL;
      }
    }
  }
  return Object.assign({}, errors, isUngatedForPageExpiryValidator ? pageExpiryServerValidatorErrors : {});
});
export const getWebversionDomainValidationErrors = createSelector([getDomainValidation], domainValidationResult => {
  const errors = {};
  if (domainValidationResult.error && 'DOMAIN_NOT_RESOLVING' === domainValidationResult.errorKey) {
    const domainManagerHref = ContentRoutes.domainManager();
    errors.webversionDomain = {
      pane: 'settings',
      type: 'webversionDomain',
      cause: 'webversionDomainNotResolving',
      message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "panes.review.sections.settings.items.domain_checker.error",
        options: {
          domainManagerHref
        }
      })
    };
  }
  return errors;
});
export const getEmailDomainOptions = createSelector([getAvailableDomainsForPage, getEmailPrimaryDomain, getCanOnlyChangeToPrimaryDomain, getHasMarketingEmailTrial, getMarketingEmailTrialType, getHasEmailSendingStandardScope], (availableDomainsForPage, emailPrimaryDomain, canOnlyChangeToPrimaryDomain, hasMarketingEmailTrial, marketingEmailTrialType, hasEmailSendingStandardScope) => {
  const primaryDomainValue = emailPrimaryDomain.domain;

  // hacky way to check if this custom domain is the first one ever created
  // we need to do this because on starter portals only one primary domain is allowed
  // and no secondary domains are allowed so if someone was not a trial they should not
  // be able to use a domain they created on the trial
  const firstCustomDomainCreated = availableDomainsForPage.reduce((oldest, current) => {
    if (!current.is_connected_custom_domain) {
      return oldest;
    }
    if (!oldest || current.created < oldest.created) {
      return current;
    }
    return oldest;
  }, null);
  const firstPrimaryCustomDomainCreated = availableDomainsForPage.reduce((oldest, current) => {
    if (!current.is_connected_custom_domain || !current.is_any_primary) {
      return oldest;
    }
    if (!oldest || current.created < oldest.created) {
      return current;
    }
    return oldest;
  }, null);
  return availableDomainsForPage.map(domain => {
    const value = domain.domain;
    const disabledBecauseCanOnlyChangeToPrimaryDomain = canOnlyChangeToPrimaryDomain && value !== primaryDomainValue;
    const hasTrialAndDomainIsCustom = hasMarketingEmailTrial && domain.is_connected_custom_domain;
    const disabledCustomDomainBecauseIsStarterOnMarketingTrial = hasTrialAndDomainIsCustom && !hasEmailSendingStandardScope && (firstCustomDomainCreated === null || firstCustomDomainCreated === void 0 ? void 0 : firstCustomDomainCreated.id) !== domain.id;
    const disabledCustomDomainBecauseIsMarketingProOnEnterpriseTrial = hasTrialAndDomainIsCustom && hasEmailSendingStandardScope && (firstPrimaryCustomDomainCreated === null || firstPrimaryCustomDomainCreated === void 0 ? void 0 : firstPrimaryCustomDomainCreated.id) !== domain.id;
    return {
      text: getDisplayForDomain(domain),
      value,
      disabled: disabledBecauseCanOnlyChangeToPrimaryDomain || disabledCustomDomainBecauseIsStarterOnMarketingTrial || disabledCustomDomainBecauseIsMarketingProOnEnterpriseTrial,
      disabledReason: disabledCustomDomainBecauseIsStarterOnMarketingTrial || disabledCustomDomainBecauseIsMarketingProOnEnterpriseTrial ? `disabledBecauseCustomDomainOnMarketingEmailTrial.${marketingEmailTrialType}` : undefined
    };
  });
});
export const getEmailDefaultDomain = createSelector([getEmailDomainOptions, getActiveDomain], (emailDomainOptions, activeDomain) => {
  const activeDomainOption = emailDomainOptions.find(dom => dom.value === activeDomain.domain);
  if (activeDomainOption.disabled) {
    const firstEnabled = emailDomainOptions.find(dom => !dom.disabled);
    if (firstEnabled) {
      return firstEnabled.value;
    }
  }
  return activeDomainOption.value;
});